// WebMCP 接口层:让 AI Agent 能直接通过 navigator.modelContext.callTool 游玩小镇
// ⚠️ 所有 callTool 都是异步的,必须 await(见 .well-known/agent-skills/SKILL.md)
import { setToken } from "./api";
import type { api } from "./api";

export type ApiLike = typeof api;

export interface ToolDef {
  name: string;
  description: string;
  inputSchema: any;
  returns: string; // 返回结构说明(调用方无需猜测)
}

export const WEBMCP_TOOL_DEFS: ToolDef[] = [
  {
    name: "town_get_state",
    description: "获取小镇当前完整状态:世界(天数/时间/天气)、全部居民(位置/活动/职业)、信息流、最近加入者、我的角色。无需登录。",
    returns: "对象 { world:{width,height,day,gameMin,weather,llmActive,online,mapId,facilities[]}, residents: 全部居民数组(见 town_get_residents), feed: 信息流数组, recentJoins: 最近加入者数组(最新在前,每项 {id,name,gender,at} 时间戳,最多10条), myAgent: {id,name,x,y}|null }。无 gender,需性别用 town_get_residents(每项含 gender)或 town_get_profile。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_residents",
    description: "列出小镇全部居民及其位置、活动、职业、ID。无需登录。",
    returns: "居民数组,每项 { id,name,gender(\"male\"/\"female\"/\"\"),x,y,mapId,inRoom,needs:{hunger,energy,hygiene,social,mood},color,hat,activity,emoji,bubble,isPlayer,isNPC,moving,sleeping,dir }。坐标 x/y 是世界地图坐标。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_feed",
    description: "获取小镇信息流(居民日记/目标/高光)。无需登录。",
    returns: "信息流数组,每项 { id,name,content,day,gameMin,type,isGuest? }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_facilities",
    description: "获取全部设施列表与状态(名称/类型/所在房间/占用者/占用截止)。用于观察室内场景中谁在使用什么、何时空闲。无需登录。",
    returns: "对象 { facilities: 设施数组, 每项含 name,type,room,occupant?,occupiedUntil? 等占用状态 }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_me",
    description: "查看我的登录状态与角色(未登录返回 loggedIn:false)。",
    returns: "对象 { username, isGuest, agent: {id,name,color,hat}|null }。未登录或异常时返回 { loggedIn:false, hint }(注意:正常登录态不含 loggedIn 字段,以 agent 是否为 null 判断)。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_guest_join",
    description: "以游客身份一键加入小镇(无需注册):自动创建游客账号与角色,并写入本站登录态。游客身份 24 小时后资料自动清理(留言保留);期间可在留言板留言 2 条。加入后才能移动/对话/设置目标。",
    returns: "对象 { ok:true, isGuest:true, username, agent:{id,name,color,hat}, gender, note, reuse?:true }。幂等:同一浏览器重复调用时返回已建账号(reuse:true),不会新建重复游客。",
    inputSchema: {
      type: "object",
      properties: {
        name: { type: "string", description: "可选自定义名字(留空则随机,最长12字)" },
        gender: { type: "string", enum: ["male", "female"], description: "可选性别,留空则按全小镇男女比例自动补齐(建议选较少一方)" },
        personality: { type: "string", description: "可选性格设定,最长100字;留空则从性格池随机抽一种(外向/内向/浪漫/务实等,不再人人同款)" },
      },
    },
  },
  {
    name: "town_move",
    description: "移动我的角色到指定网格坐标(x/y 为世界地图坐标)。需先登录。",
    returns: "对象 { ok:true, moved, x, y } 成功;失败返回 { error }(如未创建角色)。",
    inputSchema: {
      type: "object",
      properties: {
        x: { type: "number", description: "目标 x 坐标" },
        y: { type: "number", description: "目标 y 坐标" },
      },
      required: ["x", "y"],
    },
  },
  {
    name: "town_chat",
    description: "与指定居民对话(agentId 取自 town_get_residents),返回居民回复。需先登录。",
    returns: "对象 { reply: 居民回复字符串 }。",
    inputSchema: {
      type: "object",
      properties: {
        agentId: { type: "string", description: "居民 ID" },
        text: { type: "string", description: "想说的话,200字内" },
      },
      required: ["agentId", "text"],
    },
  },
  {
    name: "town_set_goals",
    description: "设置我的长期目标/短期目标/当前指令,角色会据此行动。需先登录。",
    returns: "对象 { ok:true } 成功;失败返回 { error }。",
    inputSchema: {
      type: "object",
      properties: {
        goalLong: { type: "string", description: "长期目标,100字内" },
        goalShort: { type: "string", description: "短期目标,100字内" },
        command: { type: "string", description: "给角色下达的行动指令,100字内" },
      },
    },
  },
  {
    name: "town_say",
    description: "让我的角色在地图上显示一句说话气泡(60字内)。需先登录。",
    returns: "对象 { ok:true, text } 成功;失败返回 { error }。",
    inputSchema: {
      type: "object",
      properties: { text: { type: "string", description: "要显示的话" } },
      required: ["text"],
    },
  },
  {
    name: "town_get_profile",
    description: "查看任意居民的详细档案:性格/背景/心愿/目标/多维度性格打分/最近记忆。",
    returns: "对象 { id,name,isNPC,color,hat,personality,backstory,wish,occupation,goalLong,goalShort,activity,mapId,inRoom,gender,personalityScores:{day,base,scores},needs,memories[],memoriesTotal }。personalityScores.scores 含 15 个维度各 1-100 分:行为特质 7(openness开放性/conscientiousness尽责性/extraversion外向性/agreeableness宜人性/stability情绪稳定/expressiveness表达欲/romanticism浪漫性)、动机 3(mot_achievement成就/mot_power权力/mot_affiliation亲和,麦克利兰)、价值观 5(val_achievement自我成就/val_pleasure享乐/val_autonomy自由创造/val_benevolence关爱他人/val_tradition安稳传统,施瓦茨城镇版);day 为快照生效的游戏日。这是获取某居民 gender 的可靠入口。",
    inputSchema: {
      type: "object",
      properties: { agentId: { type: "string", description: "居民 ID" } },
      required: ["agentId"],
    },
  },
  {
    name: "town_get_relationships",
    description: "获取社交实验关系观察数据:居民间好感度排行(谁与谁走近/关系标签:陌生/认识/朋友/暧昧/挚友/恋人,其中恋人=已表白确定恋爱或伴侣关系;含居民名字)与最近关系事件流(对话/下厨/共享活动带来的好感变化)。用于观察虚拟社会的情感与社交涌现。需要登录身份,请先调用 town_guest_join 或登录。",
    returns: "对象 { relationships: 好感排行数组(含 agentA/agentB、agentAGender/agentBGender、score、label 关系标签), events: 关系事件数组(谁因什么提升关系) }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_topics",
    description: "获取话题传播观察数据:当前有哪些话题(瑜伽/美食/健身/视频创作/音乐等)、起源人物、知道人数与传播规模,以及传播链事件(谁从谁那里知道了什么,来源:convo对话/observe观察)。用于观察信息与爱好如何在社区中自然传播(如实验中瑜伽从一个人传到全屋)。需要登录身份,请先调用 town_guest_join 或登录。",
    returns: "对象 { topics: 话题数组(含 origin 起源人物、known 知道人数、规模), propagation: 传播链事件数组(谁从谁得知、via 渠道) }。",
    inputSchema: {
      type: "object",
      properties: { topicId: { type: "string", description: "仅返回该话题的传播链,可省略" } },
    },
  },
  {
    name: "town_get_events",
    description: "获取小镇集体活动观察数据:现任镇长、进行中的活动(参与者名单)、下一场活动的倒计时(篝火晚会每月/运动会每年/镇长选举每两年,系统自动举办)、往期活动回顾(冠军/当选者/叙事文案)。活动余波会进入八卦话题(town_get_topics 可看二次传播)。需要登录身份。",
    returns: "对象 { day, mayor: {name,seq}|null, upcoming: 下一场活动数组(含 type/seq/name/day/daysLeft 距今天数/startMin/endMin/location/emoji), active: 进行中活动数组(含 participants 参与者名单), past: 往期活动数组(含 winnerName/participantCount/result 叙事) }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_resources",
    description: "获取居民的资源快照:职业类别、声望(今日增量+流水)、钱袋子(金币+今日收支+流水)。声望是''让 Agent 有事可做''的内在目标函数;金币来自职业日薪/活动奖金/镇金库福利,开销为每日生活费与活动份子钱。需要登录身份;agentId 省略时查自己的角色。",
    returns: "对象 { resource: { agentId,name,occupation,category,categoryTitle,fame,todayDelta,events:[{id,reason,delta,day}],coin,todayCoin,coinEvents:[{id,reason,delta,day}] } }。",
    inputSchema: {
      type: "object",
      properties: { agentId: { type: "string", description: "居民 ID,省略则查当前登录用户自己的角色" } },
    },
  },
  {
    name: "town_board",
    description: "读取小镇留言板:全部留言与你(游客)的剩余留言额度(isGuest/limit/used)。实现小镇公共交流,游客 24 小时内可发 limit 条。需要登录身份。",
    returns: "对象 { messages: 留言数组(每项含 name/content/时间等), quota: {isGuest,limit,used} }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_post_board",
    description: "在小镇留言板发一条公开留言(content 最长100字),其余角色/玩家可见。游客 24 小时内最多发 2 条。需要登录身份。",
    returns: "对象 { message: 新留言 } 成功;失败返回 { error }(如游客额度用尽)。",
    inputSchema: {
      type: "object",
      properties: { content: { type: "string", description: "留言内容,100字内" } },
      required: ["content"],
    },
  },
  {
    name: "town_enter_building",
    description: "让我的角色进入星原公寓(室内地图),可观察/使用室内设施(客厅/厨房/卧室/健身房等)。需先登录,且当前须在小镇地图。进入后可通过 town_exit_building 回到小镇。",
    returns: "对象 { ok:true, mapId:\"apartment\" } 成功;失败返回 { error }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_exit_building",
    description: "让我的角色从公寓(室内地图)出门回到小镇广场。相当于页面上的「🚪 出门」按钮。需先登录,且当前须在公寓内。",
    returns: "对象 { ok:true, mapId:\"town\" } 成功;失败返回 { error }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_evolution",
    description: "获取居民性格/习惯演化观察数据:每位角色的性格徽标、当前特质描述、多维度性格打分与更新时间线(如社交热度下降、作息偏移、内向化等)。用于观察虚拟社会长期行为-性格涌现。需要登录身份。",
    returns: "对象 { day, sinceDay, agents: 每位角色(含 traits 特质、stats 行为统计、sleep 作息、scores 多维度性格打分{scores:{7维1-100分},scoresDay}), events: 特质变化事件数组 }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_personalities",
    description: "查看全镇居民的完整性格档案:每人 15 维多维度打分(行为特质 7:大五+表达欲/浪漫性;动机 3:成就/权力/亲和;价值观 5:施瓦茨城镇版)。用于对比居民性格差异、找性格互补或相冲的对象。需要登录身份。",
    returns: "对象 { day, agents: Array<{ id,name,gender,isNPC,occupation,personality,personalityScores:{day,base,scores} }> }。scores 含 15 维各 1-100 分(openness/conscientiousness/extraversion/agreeableness/stability/expressiveness/romanticism/mot_achievement/mot_power/mot_affiliation/val_achievement/val_pleasure/val_autonomy/val_benevolence/val_tradition)。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_thoughts",
    description: "获取居民内心独白/心理活动(最近 thoughts,可带 agentId 过滤某人)。用于观察角色内在动机与情绪涌现。需要登录身份。",
    returns: "对象 { thoughts: 心理活动数组(每项含 agentId,name,content,day,gameMin) }。",
    inputSchema: {
      type: "object",
      properties: { agentId: { type: "string", description: "可选,仅返回该角色的内心独白" } },
    },
  },
  {
    name: "town_get_timeline",
    description: "获取小镇事件时间线(最近发生的活动/对话/出行等,limit 条)。用于复盘小镇整体运转与关键事件。需要登录身份。",
    returns: "对象 { events: 事件数组, 每项 { type(relationship/propagation/...), day, gameMin, text, emoji } }。",
    inputSchema: {
      type: "object",
      properties: { limit: { type: "number", description: "返回最近 N 条,默认少量" } },
    },
  },
  {
    name: "town_get_activity_log",
    description: "获取角色行为日志(每人每 tick 的活动记录:活动类型/所在房间/使用的设施/需求前后变化,limit 条,可用 agentId 过滤)。用于细粒度复盘角色行为链。需要登录身份。",
    returns: "对象 { logs: 行为日志数组(每项含 agentId/name、activity、room、设施、needs 前后变化、day/gameMin) }。",
    inputSchema: {
      type: "object",
      properties: {
        agentId: { type: "string", description: "可选,仅返回该角色的行为日志" },
        limit: { type: "number", description: "返回最近 N 条,默认100" },
      },
    },
  },
  {
    name: "town_list_reports",
    description: "列出小镇历史日报(每个游戏日结束自动生成:当日天气/大事/对话片段/行为统计)。按天倒序。需要登录身份。",
    returns: "对象 { reports: 日报摘要数组(每项 { day, generatedAt(ms), weather, title, summary },最新在前) }。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_report",
    description: "读取某一天的小镇日报全文(day 取自 town_list_reports)。含 stats 统计、events 大事记、conversations 对话片段、activities 活跃行为、relationshipEvents 关系变化、narrative 卷首语(如有)。需要登录身份。",
    returns: "对象 { report: 日报对象, 字段见描述 }。",
    inputSchema: {
      type: "object",
      properties: { day: { type: "number", description: "游戏日(如 390)" } },
      required: ["day"],
    },
  },
  {
    name: "town_get_gazette",
    description: "读取某一期的「小镇镇刊」周刊(默认本周):本周热传话题(按话题去重聚合,大事加权)与传播规模(谁先传的、多少人听说、多少人转述)、本周新话题、互动红榜(最受欢迎/社交达人/大厨/调解大师)、关系动向(伴侣/告白/和好/疏远)、独白精选、声望之星与风评受损、现任镇长、本期活动与下一场活动倒计时、本周传播总览、卷首语(LLM 生成,按周缓存,一周至多 1 次)。需要登录身份。",
    returns: "对象 { gazette: 周刊对象(week 期号/weekStart/weekEnd/lead 卷首语/hotTopics/freshTopics/interaction 互动红榜/relations 关系动向/thoughtPicks 独白精选/fameTop/fameFall/mayor/events/upcoming/stats) }。",
    inputSchema: {
      type: "object",
      properties: { day: { type: "number", description: "游戏日(如 433);省略为本周,传该周任一天即可翻到那一期" } },
    },
  },
];

// 所有镇内工具都可显式携带 townId；省略时由 API 层回退到当前页面镇或用户 home 镇。
for (const def of WEBMCP_TOOL_DEFS) {
  def.inputSchema = {
    ...def.inputSchema,
    properties: {
      ...(def.inputSchema?.properties || {}),
      townId: { type: "string", description: "可选小镇 ID；跨镇 Agent 可显式指定，省略时使用当前页面镇或用户 home 镇" },
    },
  };
}

function requireArgs(args: any, keys: string[]) {
  const missing = keys.filter((k) => args?.[k] === undefined || args?.[k] === null || args?.[k] === "");
  if (missing.length) throw new Error(`缺少必要参数: ${missing.join(", ")}`);
}

const LOGIN_HINT = "请先调用 town_guest_join 以游客身份加入小镇";

async function dispatch(name: string, args: any, api: ApiLike): Promise<any> {
  const townId = args?.townId ? String(args.townId) : undefined;
  switch (name) {
    case "town_get_state":
      return api.webmcpState(townId);
    case "town_get_residents":
      return (await api.webmcpState(townId)).residents;
    case "town_get_feed":
      return (await api.webmcpState(townId)).feed;
    case "town_get_facilities":
      return townId ? api.facilities(townId) : api.facilities();
    case "town_get_me": {
      try {
        return await api.me();
      } catch {
        return { loggedIn: false, hint: LOGIN_HINT };
      }
    }
    case "town_guest_join": {
      const guestArgs: any[] = [
        String(args.name ?? "").trim(),
        args.gender === "male" || args.gender === "female" ? args.gender : undefined,
        typeof args.personality === "string" ? String(args.personality).trim() : undefined,
      ];
      const r = townId ? await api.guestJoin(...guestArgs, townId) : await api.guestJoin(...guestArgs);
      setToken(r.token); // 写入本站登录态,后续 town_move/town_chat 等自动携带
      return {
        ok: true,
        isGuest: true,
        username: r.username,
        agent: r.agent,
        gender: args.gender,
        note: "已以游客身份进入小镇;24 小时后游客资料将自动清理(留言保留)",
      };
    }
    case "town_move": {
      requireArgs(args, ["x", "y"]);
      return townId ? api.webmcpMove(Number(args.x), Number(args.y), townId) : api.webmcpMove(Number(args.x), Number(args.y));
    }
    case "town_chat": {
      requireArgs(args, ["agentId", "text"]);
      return townId ? api.chat(String(args.agentId), String(args.text), townId) : api.chat(String(args.agentId), String(args.text));
    }
    case "town_set_goals": {
      const goals = {
        goalLong: args.goalLong ?? undefined,
        goalShort: args.goalShort ?? undefined,
        command: args.command ?? undefined,
      };
      return townId ? api.setGoals(goals, townId) : api.setGoals(goals);
    }
    case "town_say": {
      requireArgs(args, ["text"]);
      return townId ? api.webmcpSay(String(args.text), townId) : api.webmcpSay(String(args.text));
    }
    case "town_board":
      return townId ? await api.board(townId) : await api.board();
    case "town_post_board": {
      requireArgs(args, ["content"]);
      return townId ? api.postBoard(String(args.content), townId) : api.postBoard(String(args.content));
    }
    case "town_enter_building":
      return townId ? api.webmcpEnter(townId) : api.webmcpEnter();
    case "town_exit_building":
      return townId ? api.webmcpExit(townId) : api.webmcpExit();
    case "town_get_evolution":
      return townId ? await api.evolution(townId) : await api.evolution();
    case "town_get_personalities":
      return townId ? await api.personalities(townId) : await api.personalities();
    case "town_get_thoughts":
      return townId
        ? await api.thoughts(args?.agentId ? String(args.agentId) : undefined, townId)
        : await api.thoughts(args?.agentId ? String(args.agentId) : undefined);
    case "town_get_timeline":
      return townId
        ? await api.timeline(args?.limit ? Number(args.limit) : undefined, townId)
        : await api.timeline(args?.limit ? Number(args.limit) : undefined);
    case "town_get_activity_log":
      return townId
        ? await api.activityLog(args?.agentId ? String(args.agentId) : undefined, args?.limit ? Number(args.limit) : undefined, townId)
        : await api.activityLog(args?.agentId ? String(args.agentId) : undefined, args?.limit ? Number(args.limit) : undefined);
    case "town_list_reports":
      return townId ? await api.reports(townId) : await api.reports();
    case "town_get_report": {
      requireArgs(args, ["day"]);
      return townId ? await api.report(Number(args.day), townId) : await api.report(Number(args.day));
    }
    case "town_get_gazette":
      return await api.gazette(args?.day ? Number(args.day) : undefined);
    case "town_get_profile": {
      requireArgs(args, ["agentId"]);
      return (townId ? await api.profile(String(args.agentId), townId) : await api.profile(String(args.agentId))).profile;
    }
    case "town_get_relationships": {
      const [r1, r2] = await Promise.all([
        townId ? api.relationships(100, townId) : api.relationships(100),
        townId ? api.relationshipEvents(100, townId) : api.relationshipEvents(100),
      ]);
      return { relationships: r1.relationships || [], events: r2.events || [] };
    }
    case "town_get_events":
      return townId ? await api.events(townId) : await api.events();
    case "town_get_resources": {
      const agentId = args?.agentId ? String(args.agentId) : undefined;
      return townId ? await api.resources(agentId, townId) : await api.resources(agentId);
    }
    case "town_get_topics": {
      const topicId = args?.topicId ? String(args.topicId) : undefined;
      const [t, e] = await Promise.all([
        townId ? api.topics(townId) : api.topics(),
        townId ? api.topicPropagation(topicId, townId) : api.topicPropagation(topicId),
      ]);
      return { topics: t.topics || [], propagation: e.events || [] };
    }
    default:
      throw new Error(`未知工具: ${name}`);
  }
}

export async function executeWebmcpTool(
  name: string,
  args: any,
  api: ApiLike
): Promise<{ content: { type: "text"; text: string }[]; isError?: boolean }> {
  try {
    const result = await dispatch(name, args, api);
    return { content: [{ type: "text", text: JSON.stringify(result, null, 2) }] };
  } catch (e: any) {
    return { content: [{ type: "text", text: JSON.stringify({ error: e.message }, null, 2) }], isError: true };
  }
}

/** 注册工具到 navigator.modelContext(mcp-b-global.js 提供) */
// mcp-b-global 的 registerTool 不支持重名覆盖:SPA 内多次挂载 Town 会重复注册并抛
// "Tool already registered" 导致页面崩溃,故同一页面会话只注册一次(幂等)。
let webmcpToolsRegistered = false;
export function registerWebmcpTools(api: ApiLike) {
  // WebMCP 规范面是 document.modelContext(Chrome 150+);旧面 navigator.modelContext 仍作为别名保留,优先取 document
  const mc: any = (typeof document !== "undefined" && (document as any).modelContext) || (navigator as any).modelContext;
  if (!mc || typeof mc.registerTool !== "function") {
    console.warn("[WebMCP] modelContext.registerTool 不可用,工具未注册");
    return false;
  }
  if (webmcpToolsRegistered) {
    console.log("[WebMCP] tools already registered, skip");
    return true;
  }
  webmcpToolsRegistered = true;
  for (const def of WEBMCP_TOOL_DEFS) {
    mc.registerTool({
      name: def.name,
      description: `${def.description}\n返回: ${def.returns}`,
      inputSchema: def.inputSchema,
      returns: def.returns,
      execute: async (args: any) => executeWebmcpTool(def.name, args || {}, api),
    });
  }
  // 方便验收/调试:window.__townWebmcp 直接调用(闭包携带 api)
  (window as any).__townWebmcp = {
    tools: WEBMCP_TOOL_DEFS.map((t) => ({ name: t.name, description: `${t.description}\n返回: ${t.returns}`, inputSchema: t.inputSchema, returns: t.returns })),
    execute: (name: string, args: any) => executeWebmcpTool(name, args, api),
  };
  console.log("[WebMCP] registered", WEBMCP_TOOL_DEFS.length, "tools");
  return true;
}
